import type { FormEvent } from 'react';
import { Switch } from '../../../ui/Switch';
import type { Member } from '../../../lib/api';
import { ASSIGN_HINT, ASSIGN_OPTIONS, CADENCE_OPTIONS, FREQ_OPTIONS, RECUR_OPTIONS, type AssigneeChoice, type AssignMode, type ChoreItem, type GoogleStatus, type RecurMode } from './types';

/** The add/edit surface (cards VmoORelj1HuO / UeOwZSHfdZCh / eudAJs92EZ9B) —
 *  every field the composer owns, passed down as props by index.tsx. */
export function ChoreComposer({
  editing, title, setTitle, cadence, setCadence, freq, setFreq, due, setDue, time, setTime,
  assignee, setAssignee, assignMode, setAssignMode, assignModePicked, setAssignModePicked,
  recurMode, setRecurMode, every, setEvery, adding, everyDays, timeWithoutDate,
  google, addToGoogle, setAddToGoogle, submitChore, resetComposer, otherMember, partnerName,
}: {
  editing: ChoreItem | null;
  title: string;
  setTitle: (v: string) => void;
  cadence: 'once' | 'recurring';
  setCadence: (v: 'once' | 'recurring') => void;
  freq: string;
  setFreq: (v: string) => void;
  due: string;
  setDue: (v: string) => void;
  time: string;
  setTime: (v: string) => void;
  assignee: AssigneeChoice;
  setAssignee: (v: AssigneeChoice) => void;
  assignMode: AssignMode;
  setAssignMode: (v: AssignMode) => void;
  assignModePicked: boolean;
  setAssignModePicked: (v: boolean) => void;
  recurMode: RecurMode;
  setRecurMode: (v: RecurMode) => void;
  every: string;
  setEvery: (v: string) => void;
  adding: boolean;
  everyDays: number | null;
  timeWithoutDate: boolean;
  google: GoogleStatus | null;
  addToGoogle: boolean;
  setAddToGoogle: (v: boolean) => void;
  submitChore: (e: FormEvent) => Promise<void>;
  resetComposer: () => void;
  otherMember: Member | null;
  partnerName: string;
}) {
  return (
    <form className="rq-card ch-composer" onSubmit={submitChore}>
      <label className="rq-composer-label" htmlFor="ch-input">
        {editing ? 'Edit task' : 'Add a task'}
      </label>
      <input
        id="ch-input"
        type="text"
        className="pear-chat-input rq-input"
        placeholder="e.g. bins out on Tuesday"
        aria-label="New task"
        value={title}
        onChange={e => setTitle(e.target.value)}
      />

      {/* Single-door add: one field and Add are always on screen; who / how
          often live behind Details so a one-off is one field + one tap. */}
      <details className="ch-details" id="ch-details">
        <summary>Who &amp; repeats <span className="ch-details-hint">optional</span></summary>
      {/* Which rule owns the assignee (card UeOwZSHfdZCh): fixed keeps who
          you pick, claim is taken by whoever taps first, alternate passes
          to the partner on every completion. */}
      <div className="ch-composer-row" role="group" aria-label="Who decides">
        {ASSIGN_OPTIONS.map(opt => (
          <button
            key={opt.value}
            type="button"
            aria-pressed={assignMode === opt.value}
            className={`underline-select${assignMode === opt.value ? ' is-on' : ''}`}
            onClick={() => { setAssignMode(opt.value); setAssignModePicked(true); }}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <p className="rq-empty-hint ch-assign-hint" id="ch-assign-hint">
        {ASSIGN_HINT[assignMode]}
      </p>

      {/* Who is on it — decided when the chore is created, so the row it
          lands in is already assigned (card eudAJs92EZ9B). The partner
          segment only exists once a second member has joined. Hidden while
          the mode is Claim: nobody is on a claim chore until they take it,
          so there is nothing here to pick (card UeOwZSHfdZCh). */}
      {assignMode !== 'claim' && (
      <div className="ch-composer-row" role="group" aria-label="Who is on it">
        <button
          type="button"
          aria-pressed={assignee === 'me'}
          className={`underline-select${assignee === 'me' ? ' is-on' : ''}`}
          onClick={() => setAssignee('me')}
        >
          Me
        </button>
        {otherMember && (
          <button
            type="button"
            aria-pressed={assignee === 'partner'}
            className={`underline-select${assignee === 'partner' ? ' is-on' : ''}`}
            onClick={() => setAssignee('partner')}
          >
            {partnerName}
          </button>
        )}
        <button
          type="button"
          aria-pressed={assignee === 'anyone'}
          className={`underline-select${assignee === 'anyone' ? ' is-on' : ''}`}
          onClick={() => setAssignee('anyone')}
        >
          Anyone
        </button>
      </div>
      )}

      <div className="ch-composer-row" role="group" aria-label="Cadence">
        {CADENCE_OPTIONS.map(opt => (
          <button
            key={opt.value}
            type="button"
            aria-pressed={cadence === opt.value}
            className={`underline-select${cadence === opt.value ? ' is-on' : ''}`}
            onClick={() => {
              setCadence(opt.value);
              // A NEW recurring chore takes turns by default (card
              // kZX3gsJkKaTH): the roll-forward already alternates on
              // completion, so the pair never needs a fairness reading to
              // stay even. A one-off has no next round to hand over, so it
              // stays Fixed. Anyone who has picked a mode keeps it.
              if (!assignModePicked) setAssignMode(opt.value === 'recurring' ? 'alternate' : 'fixed');
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {cadence === 'recurring' && (
        <>
          {/* Which rule (card VmoORelj1HuO): a cadence, a straight N-day
              interval, or a step counted from the tick. */}
          <div className="ch-composer-row" role="group" aria-label="Repeats how">
            {RECUR_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                aria-pressed={recurMode === opt.value}
                className={`underline-select${recurMode === opt.value ? ' is-on' : ''}`}
                onClick={() => setRecurMode(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {recurMode === 'fixed' && (
            <div className="ch-composer-row" role="group" aria-label="Repeats">
              {FREQ_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  aria-pressed={freq === opt.value}
                  className={`underline-select${freq === opt.value ? ' is-on' : ''}`}
                  onClick={() => setFreq(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          {recurMode !== 'fixed' && (
            <div className="ch-composer-row ch-every-row" role="group" aria-label="Step in days">
              <label className="ch-due-label" htmlFor="ch-every">
                {recurMode === 'interval' ? 'Every' : 'Days after it is done'}
              </label>
              <input
                id="ch-every"
                type="number"
                inputMode="numeric"
                min={1}
                max={365}
                className="pear-chat-input ch-every"
                aria-label={recurMode === 'interval' ? 'Interval in days' : 'Days after completion'}
                value={every}
                onChange={e => setEvery(e.target.value)}
              />
              <span className="rq-empty-hint">
                {recurMode === 'interval'
                  ? 'days, kept on schedule'
                  : 'days after the last tick'}
              </span>
            </div>
          )}
        </>
      )}

      {/* Add to Calendar & Tasks (card eudAJs92EZ9B) — the switch the card
          asks for at creation, wired to the OAuth shipped in hWnpJn7Tfl38.
          Only rendered on builds that carry a Google client, so the panel
          never offers a switch that cannot do anything. Lives inside Details:
          it is a sync preference, not part of the one-tap add. */}
      {google?.configured && (
        <div className="ch-composer-row ch-sync-row" id="ch-sync-row">
          <span className="rq-composer-label">Add to Calendar &amp; Tasks</span>
          <Switch
            checked={addToGoogle}
            onChange={setAddToGoogle}
            label="Add to Calendar and Tasks"
          />
          {!google.connected && (
            <span className="rq-empty-hint ch-sync-hint">
              Link Google below and it goes across.
            </span>
          )}
        </div>
      )}
      </details>

      <div className="ch-composer-foot">
        {/* One field = one label stacked on its own picker (Jonathan
            2026-10-20). Label and input used to be flat siblings of the whole
            row, so a wrap could leave "Specify time" on one line with its
            time picker on the next, and Add hanging against a label instead
            of a field. */}
        <div className="ch-field">
          <label className="ch-due-label" htmlFor="ch-due">
            {cadence === 'recurring' ? 'First due' : 'Due'}
          </label>
          <input
            id="ch-due"
            type="date"
            className="pear-chat-input ch-due"
            aria-label="Due date"
            value={due}
            onChange={e => setDue(e.target.value)}
          />
        </div>
        <div className="ch-field">
          {/* Specify Time (card eudAJs92EZ9B) — a clock next to the date.
              Always live: gating it on the date first meant a tap on the
              clock did nothing, so nobody could set a time (founder report
              2026-10-08). Add takes the date requirement instead. */}
          <label className="ch-due-label" htmlFor="ch-time">
            Specify time
          </label>
          <input
            id="ch-time"
            type="time"
            className="pear-chat-input ch-due ch-time"
            aria-label="Specify time"
            aria-describedby={timeWithoutDate ? 'ch-time-hint' : undefined}
            value={time}
            onChange={e => setTime(e.target.value)}
          />
        </div>
        {timeWithoutDate && (
          <span className="rq-empty-hint ch-time-hint" id="ch-time-hint">
            Pick a date for that time.
          </span>
        )}
        <button
          type="submit"
          className="pear-btn pear-btn-mustard ch-add"
          disabled={adding || title.trim().length === 0 || timeWithoutDate
            || (cadence === 'recurring' && recurMode !== 'fixed' && everyDays === null)}
        >
          {adding ? (editing ? 'Saving…' : 'Adding…') : editing ? 'Save' : 'Add'}
        </button>
        {editing && (
          <button
            type="button"
            id="ch-cancel"
            className="pear-btn ch-cancel"
            disabled={adding}
            onClick={resetComposer}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
